import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CompanyCityDto, NationalAddressDto } from 'src/app/core/data-models/national-address.data.models';
import { normalizeNationalAddressFromApi } from 'src/app/shared/utils/first-step-payload';
import { CompanyDataService } from 'src/app/core/data-services/Company/company.data.service';
import { NotificationService } from 'src/app/core/common-services/notification.service';

/** `company` = company CR national address (city only); `applicant` = logged-in user's personal national address. */
export type NationalAddressSource = 'company' | 'applicant';

@Component({
  selector: 'app-national-address',
  templateUrl: './national-address.component.html',
  styleUrls: ['./national-address.component.css']
})
export class NationalAddressComponent implements OnInit, OnChanges {
  @Input() formData?: NationalAddressDto | CompanyCityDto | null;
  @Output() previous = new EventEmitter<void>();
  @Output() submit = new EventEmitter<NationalAddressDto | CompanyCityDto>();
  @Input() isDetails: boolean = false;
  /** Which API to load defaults from */
  @Input() addressSource: NationalAddressSource = 'company';
  /** Supplier use-case: render full company national address fields instead of city-only. */
  @Input() companyFullAddress = false;

  form!: FormGroup;

  get isCompany(): boolean {
    return this.addressSource === 'company';
  }

  get isCompanyCityOnly(): boolean {
    return this.isCompany && !this.companyFullAddress;
  }

  constructor(
    private fb: FormBuilder,
    private clientInfoService: CompanyDataService,
    private notificationService: NotificationService
  ) {}

  ngOnInit() {
    this.initForm();
    if (this.formData) {
      this.patchAddress(this.formData);
      return;
    }
    if (this.isCompany && this.companyFullAddress) {
      this.loadCompanyNationalAddress();
      return;
    }
    if (!this.isCompany) {
      this.loadNationalAddress();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.form) return;
    if (changes['formData']?.currentValue) {
      this.patchAddress(changes['formData'].currentValue as NationalAddressDto | CompanyCityDto);
    }
  }

  private initForm() {
    if (this.isCompanyCityOnly) {
      this.form = this.fb.group({
        city: [
          {
            value: this.normalizeCompanyCity((this.formData as CompanyCityDto | null | undefined) ?? null),
            disabled: this.isDetails,
          },
          Validators.required,
        ],
      });
    } else {
      const d = this.formData as NationalAddressDto | undefined | null;
      this.form = this.fb.group({
        buildingNumber: [{ value: d?.buildingNumber || '', disabled: this.isDetails }, Validators.required],
        street: [{ value: d?.street || '', disabled: this.isDetails }, Validators.required],
        additionalNumber: [{ value: d?.additionalNumber || '', disabled: this.isDetails }, Validators.required],
        district: [{ value: d?.district || '', disabled: this.isDetails }, Validators.required],
        city: [{ value: d?.city || '', disabled: this.isDetails }, Validators.required],
        postalCode: [{ value: d?.postalCode || '', disabled: this.isDetails }, Validators.required],
        unitNumber: [{ value: d?.unitNumber || '', disabled: this.isDetails }, Validators.pattern(/^\d*$/)],
      });
    }
  }

  private loadNationalAddress() {
    this.clientInfoService.GetUserNationalAddress().subscribe({
      next: (res) => {
        if (res?.success && res.resource) {
          this.patchAddress(res.resource as NationalAddressDto);
        }
      },
      error: () => {
        this.notificationService.error('Error loading national address:');
      },
    });
  }

  private loadCompanyNationalAddress() {
    this.clientInfoService.GetNationalAddress().subscribe({
      next: (res) => {
        if (res?.success && res.resource) {
          this.patchAddress(res.resource as NationalAddressDto);
        }
      },
      error: () => {
        this.notificationService.error('Error loading national address:');
      },
    });
  }

  getFormData(): NationalAddressDto | CompanyCityDto | null {
    if (this.form.valid) return this.form.value;
    this.form.markAllAsTouched();
    return null;
  }

  onUnitNumberInput(): void {
    const c = this.form.get('unitNumber');
    if (!c) return;
    const numeric = (c.value ?? '').toString().replace(/\D/g, '');
    c.setValue(numeric, { emitEvent: false });
  }

  private patchAddress(r: NationalAddressDto | CompanyCityDto): void {
    if (this.isCompanyCityOnly) {
      const raw = r as CompanyCityDto & { City?: string | null };
      this.form.patchValue({ city: this.normalizeCompanyCityRaw(raw.city ?? raw.City) });
    } else {
      const addr = normalizeNationalAddressFromApi(r as NationalAddressDto);
      if (!addr) return;
      this.form.patchValue({
        buildingNumber: addr.buildingNumber ?? '',
        street: addr.street ?? '',
        additionalNumber: addr.additionalNumber ?? '',
        district: addr.district ?? '',
        city: addr.city ?? '',
        postalCode: addr.postalCode ?? '',
        unitNumber:
          addr.unitNumber !== undefined && addr.unitNumber !== null ? String(addr.unitNumber) : '',
      });
    }
  }

  /** API may send `null`, string `"null"`, or PascalCase `City`. */
  private normalizeCompanyCity(dto: CompanyCityDto | null | undefined): string {
    if (!dto) return '';
    const raw = (dto as CompanyCityDto & { City?: string | null }).city ?? (dto as { City?: string | null }).City;
    return this.normalizeCompanyCityRaw(raw);
  }

  private normalizeCompanyCityRaw(raw: unknown): string {
    if (raw === null || raw === undefined) return '';
    const s = String(raw).trim();
    if (!s || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') return '';
    return s;
  }
}
